/// <reference types="vite/client" />
import { parseAppEnvironment, type AppEnvironment } from "./app-environment";
import liveBooks from "./live-books.json";

export const SITE_NAME = "Yarn";
export const SITE_TAGLINE = "Follow every character and place through the story on an interactive timeline.";

export interface SeriesEntry {
  id: string;
  title: string;
  /** The address of this series. Permanent, like a book's. */
  slug: string;
  /** A one-off book: it sits under Books on the homepage, with no series heading. */
  standalone?: boolean;
}

const ALL_SERIES: readonly SeriesEntry[] = [
  { id: "harry-potter", title: "Harry Potter", slug: "harry-potter" },
  { id: "animorphs", title: "Animorphs", slug: "animorphs" },
  { id: "back-to-the-future", title: "Back to the Future", slug: "back-to-the-future" },
];

export interface BookEntry {
  id: string;
  seriesId: string;
  /** Its place in the series, from 1. */
  seriesOrder: number;
  /** Who wrote it and who owns it, for the credit on its page and the legal page. */
  credit: string;
  /** The full title, as the top bar shows it. */
  title: string;
  /** A shorter title where room is tight, such as the phone's top bar. */
  shortTitle?: string;
  /** This book's part of the address, after its series. Permanent: changing it costs ranking. */
  slug: string;
  /** A book unless it says otherwise. It decides words like "chapter" or "part". */
  medium?: "book" | "movie";
}

const WORDS = {
  book: { whole: "book", part: "chapter", parts: "Chapters" },
  movie: { whole: "movie", part: "part", parts: "Parts" },
} as const;

/** The words for a book or a movie: "Play the whole movie", "3 parts". */
export const mediumWords = (book: Pick<BookEntry, "medium">) => WORDS[book.medium ?? "book"];


export const ALL_BOOKS: readonly BookEntry[] = [
  {
    id: "hp01",
    seriesId: "harry-potter",
    seriesOrder: 1,
    credit: "By J.K. Rowling. Published in the US by Scholastic. Film images from the Harry Potter "
      + "films by Warner Bros. Pictures. Harry Potter characters and names are trademarks of "
      + "Warner Bros. Entertainment Inc. Peeves art by Bob Venables.",
    title: "Harry Potter and the Sorcerer's Stone",
    shortTitle: "Sorcerer's Stone",
    slug: "sorcerers-stone",
  },
  {
    id: "animorphs01",
    seriesId: "animorphs",
    seriesOrder: 1,
    credit: "By K.A. Applegate. Published by Scholastic. Cover art from the Scholastic editions. "
      + "Antarean Bogg art by monster-man-08 on DeviantArt. "
      + "Dude, Chapman, and Tom art by Chris Grine, from the Animorphs graphic novel. "
      + "Hork-Bajir, Taxxon, Andalite, ship, and Visser Three morph art from the official "
      + "Animorphs licensee illustrations.",
    title: "The Invasion",
    slug: "the-invasion",
  },
  {
    id: "animorphs02",
    seriesId: "animorphs",
    seriesOrder: 2,
    credit: "By K.A. Applegate. Published by Scholastic. Cover art from the Scholastic editions. "
      + "Chapman, Ms. Chapman, Melissa, and Tom art by Chris Grine, from the Animorphs "
      + "graphic novel. "
      + "Photos from Wikimedia Commons: bald eagle by Saffron Blaze (CC BY-SA 3.0), "
      + "peregrine falcon by Dhairya dixit (CC BY-SA 4.0), osprey by Andy Morffew (CC BY 2.0), "
      + "shrew by Gilles Gonthier (CC BY 2.0), "
      + "flea by Fedaro (CC BY-SA 4.0), earthmover by Frank Vincentz (CC BY-SA 3.0).",
    title: "The Visitor",
    slug: "the-visitor",
  },
  {
    id: "bttf01",
    seriesId: "back-to-the-future",
    seriesOrder: 1,
    credit: "Directed by Robert Zemeckis. Written by Robert Zemeckis and Bob Gale. "
      + "Universal Pictures. Portraits are stills from the film. Poster art by Drew Struzan.",
    title: "Back to the Future",
    slug: "back-to-the-future",
    medium: "movie",
  },
];

/** The app's own build, or a script told which site it is building for. */
function buildEnvironment(): AppEnvironment {
  const fromScript = (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process?.env?.VITE_APP_ENV;
  return parseAppEnvironment((import.meta.env?.VITE_APP_ENV as string | undefined) ?? fromScript);
}

/** Every book is available while building them. Production has only those marked live. */
export function booksForEnvironment<T extends { id: string }>(
  books: readonly T[],
  liveIds: readonly string[],
  environment: AppEnvironment,
): readonly T[] {
  return environment === "production"
    ? books.filter((book) => liveIds.includes(book.id))
    : books;
}

export const BOOKS = booksForEnvironment(ALL_BOOKS, liveBooks.live, buildEnvironment());

/** A series shows only once it has a book available. */
export const SERIES = ALL_SERIES.filter((series) => (
  BOOKS.some((book) => book.seriesId === series.id)
));

/** The book scripts act on when none is named. */
export const DEFAULT_BOOK_ID = "hp01";

function seriesSlugOf(book: BookEntry): string {
  return ALL_SERIES.find((series) => series.id === book.seriesId)?.slug ?? book.seriesId;
}

/** The page about a book, inside its series. */
export function bookPagePath(book: BookEntry): string {
  return `/${seriesSlugOf(book)}/${book.slug}`;
}

/** The whole book playing. */
export function bookPath(book: BookEntry): string {
  return `${bookPagePath(book)}/play`;
}

export function seriesPath(series: SeriesEntry): string {
  return `/${series.slug}`;
}

export function booksInSeries(seriesId: string): BookEntry[] {
  return BOOKS.filter((book) => book.seriesId === seriesId)
    .sort((a, b) => a.seriesOrder - b.seriesOrder);
}

export function chapterPath(book: BookEntry, chapterNumber: number): string {
  return `${bookPagePath(book)}/chapter-${chapterNumber}`;
}

/** A character's page belongs to their series, so it follows them from book to book. */
export function characterPath(book: BookEntry, characterId: string): string {
  return `/${seriesSlugOf(book)}/characters/${characterId}`;
}

const COMPENDIUM_FOLDERS = {
  race: "races",
  vehicle: "vehicles",
  organization: "organizations",
  business: "businesses",
  spell: "spells",
  morph: "morphs",
  item: "items",
} as const;

/** A compendium page belongs to its series, like a character's. */
export function compendiumPath(
  book: BookEntry,
  id: string,
  kind: keyof typeof COMPENDIUM_FOLDERS,
): string {
  return `/${seriesSlugOf(book)}/${COMPENDIUM_FOLDERS[kind]}/${id}`;
}

/** The book an address names, from its series and book parts. */
export function bookFromSlugs(
  seriesSlug: string | undefined,
  bookSlug: string | undefined,
): BookEntry | undefined {
  return BOOKS.find((book) => book.slug === bookSlug && seriesSlugOf(book) === seriesSlug);
}

/** "chapter-5" to chapter 5, or null for anything else. */
export function chapterFromSlug(slug: string): number | null {
  const match = /^chapter-([1-9]\d*)$/.exec(slug);
  return match === null ? null : Number(match[1]);
}

export function pageTitle(...parts: readonly string[]): string {
  return [...parts, SITE_NAME].join(" | ");
}
